import type { ChannelConfig, GoalConfig, Pin } from "./config";

/**
 * Flashadz's starting values for a new goal. Every one is a starting point the owner can change on the
 * goal afterwards; none is read anywhere else. They match the default playbook (stop an ad at twice the
 * most per result, raise a daily amount about 20% at a time) and the prototype's sample settings.
 */
export const GOAL_DEFAULTS = {
  timezone: "America/New_York",
  stopNoResultMultiple: 2,
  stepUpPct: 20,
  patienceDays: 7,
  patienceClicks: { min: 30, max: 400 },
  /** The share of a total budget the tests get, as the playbooks split it. */
  testShare: 1 / 3,
  /** A typical cost per click (LocaliQ, September 2026, lead campaigns: $1.80). */
  cpcCents: 180,
  callingAndTexting: false,
  never: ["Guessing email addresses", "Bought lists, or uploading any list except the owner's own client lists, added with consent"],
  review: { kind: "daily", at: "09:00" } as const,
  replies: { kind: "every", minutes: 120 } as const,
  liveAdChangeDays: 3,
  sweep: { kind: "weekly", day: 0, at: "14:00" } as const,
  perRunMicros: 500_000,
} as const;

export interface NewGoalInput {
  sentence: string;
  label: string;
  target: number;
  by: string;
  budgetCents: number;
  perResultMaxCents: number;
  dailyMaxCents: number | null;
  timezone?: string;
  /** Channel id to on or off, from the goal step. */
  channels: Record<string, boolean>;
  /** The page the result happens on: a Meta goal's ads send people there. */
  link?: string;
  library: Pin[];
}

/**
 * When a test may be judged: 7 days and the clicks the test money buys at a typical cost per click, between 30
 * and 400. Why: one fixed 400 clicks is out of reach for a small budget, so no test would ever be judged.
 */
export function patienceFor(budgetCents: number): { days: number; clicks: number } {
  const d = GOAL_DEFAULTS;
  const clicks = Math.round((budgetCents * d.testShare) / d.cpcCents);
  return { days: d.patienceDays, clicks: Math.min(d.patienceClicks.max, Math.max(d.patienceClicks.min, clicks)) };
}

/** The first config of a goal, from what the owner typed on the goal step plus the starting values. */
export function initialGoalConfig(input: NewGoalInput): GoalConfig {
  const d = GOAL_DEFAULTS;
  const channels: Record<string, ChannelConfig> = {};
  for (const [id, on] of Object.entries(input.channels)) {
    channels[id] = { on, card: null, rules: [], markets: [], settings: id === "meta" && input.link ? { link: input.link } : {} };
  }
  return {
    v: 1,
    sentence: input.sentence,
    result: { label: input.label, target: input.target, by: input.by },
    money: {
      budgetCents: input.budgetCents,
      perResultMaxCents: input.perResultMaxCents,
      dailyMaxCents: input.dailyMaxCents,
      toolsCents: 0,
    },
    timezone: input.timezone || d.timezone,
    rules: {
      stopNoResultCents: input.perResultMaxCents * d.stopNoResultMultiple,
      patience: patienceFor(input.budgetCents),
      stepUpPct: d.stepUpPct,
      callingAndTexting: d.callingAndTexting,
      never: [...d.never],
      own: [],
    },
    rhythm: {
      review: { ...d.review },
      replies: { ...d.replies },
      liveAdChangeDays: d.liveAdChangeDays,
      sweep: { ...d.sweep },
    },
    channels,
    library: input.library,
    tools: {},
    caps: { perRunMicros: d.perRunMicros },
    autonomy: {},
    models: {},
  };
}

/**
 * Starting values for a goal's Meta ads (GoalConfig.channels.meta.settings), each one the goal can
 * change. No place: who sees the ads is the goal's own audience, from its plan.
 */
export const META_DEFAULTS = {
  callToAction: "SIGN_UP",
  optimise: { goal: "lead_conversion", event: "LEAD" },
} as const;

/**
 * Starting values for a goal's cold email (GoalConfig.channels.email.settings), each one the goal can
 * change: Smartlead sends on weekdays in business hours, in the goal's own timezone, and appends its
 * unsubscribe link with these words.
 */
export const EMAIL_DEFAULTS = {
  days: ["mon", "tue", "wed", "thu", "fri"],
  startHour: "09:00",
  endHour: "17:00",
  /** Minutes between two sends from one mailbox: 30 a day fit an 8-hour window. */
  minTimeBetweenEmails: 15,
  /** New people a day per market: the cold-email skill's slow start (5 to 10 a day per new mailbox, follow-ups included). */
  newPerDay: 5,
  unsubscribeText: "Unsubscribe",
} as const;
